/* ==========================================================================
   CLIBINO · Aroma — global.js
   Interacciones transversales: menú móvil, acordeónes, filtro de reseñas,
   animaciones de scroll y contadores. La barra de anuncios ya no rota por
   JS: es una cinta continua animada por CSS (ver component-header.css).
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  initMobileNav();
  initAccordions();
  initReviewFilters();
  initProductGallery();
  initScrollReveal();
  initHeaderShadow();
  initCounters();
});

/* --- Scroll reveal ---
   Fade + lift sutil para TODAS las secciones de confianza al entrar en
   viewport. El retraso se calcula por posición entre hermanos del mismo
   bloque, así cada tarjeta de una fila aparece en cascada. */
function initScrollReveal() {
  if (!('IntersectionObserver' in window)) return;

  const selector = [
    '.how-it-works__step', '.accordion-item', '.pack-tier', '.pack-picker__card',
    '.pdp-final-cta__benefits > li', '.trust-bar', '.trust-bar__item',
    '.cb-ben__card', '.cb-prob__list li', '.cb-prob__media', '.cb-cmp__scroll',
    '.cb-steps__card', '.review-card', '.problem-card', '.feature',
    '.review-row', '.card', '.swatch', '.cb-trust__stat',
  ].join(', ');

  const targets = document.querySelectorAll(selector);
  if (!targets.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  requestAnimationFrame(() => {
    const siblingIndex = new WeakMap();
    targets.forEach((el) => {
      const parent = el.parentElement;
      const i = siblingIndex.get(parent) || 0;
      siblingIndex.set(parent, i + 1);
      el.classList.add('reveal');
      el.style.transitionDelay = Math.min(i, 5) * 70 + 'ms';
      observer.observe(el);
    });
  });
}

/* --- Contadores animados ---
   Cuenta desde 0 hasta el número objetivo cuando el bloque entra en
   viewport (cifras de confianza). Respeta prefers-reduced-motion. */
function initCounters() {
  const counters = document.querySelectorAll('[data-counter]');
  if (!counters.length) return;

  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function animateCounter(el) {
    const target = parseFloat(el.dataset.target) || 0;
    const prefix = el.dataset.prefix || '';
    const suffix = el.dataset.suffix || '';
    const isDecimal = String(target).includes('.');
    if (reduce) {
      el.textContent = prefix + target + suffix;
      return;
    }
    const duration = 1200;
    const start = performance.now();
    function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = target * eased;
      el.textContent = prefix + (isDecimal ? value.toFixed(1) : Math.round(value)) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  if (!('IntersectionObserver' in window)) {
    counters.forEach(animateCounter);
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        animateCounter(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.4 });
  counters.forEach((el) => observer.observe(el));
}

/* --- Sombra de cabecera al hacer scroll --- */
function initHeaderShadow() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const update = () => header.classList.toggle('has-shadow', window.scrollY > 4);
  update();
  window.addEventListener('scroll', update, { passive: true });
}

/* --- Galería de producto: carrusel deslizable ---
   La imagen principal ya no es un <img> único al que se le cambia el src:
   es un carrusel de scroll nativo + scroll-snap (mismo mecanismo que ya
   usan las reseñas y los vídeos), así el swipe táctil funciona gratis en
   cualquier navegador. Las miniaturas y el swatch de color ahora navegan
   desplazando el track hasta el slide correspondiente en vez de sustituir
   una imagen suelta. */
function initProductGallery() {
  const track = document.querySelector('[data-gallery-track]');
  const slides = track ? Array.from(track.querySelectorAll('[data-gallery-slide]')) : [];
  const thumbs = Array.from(document.querySelectorAll('[data-thumb]'));
  if (!track || !slides.length) return;

  function goToIndex(index) {
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    track.scrollTo({ left: clamped * track.clientWidth, behavior: 'smooth' });
  }
  function currentIndex() {
    const width = track.clientWidth;
    return width > 0 ? Math.round(track.scrollLeft / width) : 0;
  }
  function syncThumbs() {
    const i = currentIndex();
    thumbs.forEach((t, ti) => t.classList.toggle('is-active', ti === i));
  }
  function goToSrc(src) {
    if (!src) return;
    const index = slides.findIndex((s) => s.dataset.mediaSrc === src);
    if (index > -1) goToIndex(index);
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => goToIndex(parseInt(thumb.dataset.index, 10) || 0));
  });

  if (slides.length > 1) {
    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(syncThumbs, 100);
    }, { passive: true });

    window.addEventListener('resize', () => {
      track.scrollLeft = currentIndex() * track.clientWidth;
    });
  }

  document.querySelectorAll('[data-variant-image]').forEach((input) => {
    input.addEventListener('change', () => goToSrc(input.dataset.variantImage));
  });
}

/* --- Menú móvil --- */
function initMobileNav() {
  const burger = document.querySelector('[data-mobile-nav-open]');
  const nav = document.querySelector('[data-mobile-nav]');
  const closeBtn = document.querySelector('[data-mobile-nav-close]');
  if (!burger || !nav) return;
  burger.addEventListener('click', () => nav.classList.add('is-open'));
  closeBtn?.addEventListener('click', () => nav.classList.remove('is-open'));
}

/* --- Acordeónes reutilizables --- */
function initAccordions() {
  document.querySelectorAll('.accordion-item__trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      const panel = item.querySelector('.accordion-item__panel');
      const isOpen = item.classList.contains('is-open');

      const group = item.closest('[data-accordion-group]');
      if (group) {
        group.querySelectorAll('.accordion-item.is-open').forEach((openItem) => {
          if (openItem !== item) {
            openItem.classList.remove('is-open');
            openItem.querySelector('.accordion-item__panel').style.maxHeight = null;
          }
        });
      }

      if (isOpen) {
        item.classList.remove('is-open');
        panel.style.maxHeight = null;
      } else {
        item.classList.add('is-open');
        panel.style.maxHeight = panel.scrollHeight + 'px';
      }
    });
  });
}

/* --- Filtro de reseñas --- */
function initReviewFilters() {
  const buttons = document.querySelectorAll('.reviews-filter button');
  const rows = document.querySelectorAll('.review-row');
  if (!buttons.length) return;

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      buttons.forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const filter = btn.dataset.filter;

      rows.forEach((row) => {
        const tags = (row.dataset.tags || '').split(',');
        const show = filter === 'all' || tags.includes(filter);
        row.classList.toggle('is-visible', show);
      });
    });
  });
}

/* Utilidad compartida para formatear precios en céntimos -> "12,34 €" */
window.AmansaFormatMoney = function (cents, symbol = '€') {
  return (cents / 100).toFixed(2).replace('.', ',') + ' ' + symbol;
};
